import React, { useState, useEffect } from 'react';
import { Form, Icon, Progress, Button } from 'antd';
import PropTypes from 'prop-types';
import { validatePassword } from '../utils/validation';
import {
    PageShell,
    FormCard,
    StepBadge,
    CardTitle,
    CardSubtitle,
    FieldLabel,
    SummaryBox,
    ReqList,
    ReqItem,
    StyledInput,
    StyledButton,
} from '../styled';

const REQUIREMENTS = [
    { key: 'length', label: 'Pelo menos 8 caracteres', test: (p) => p.length >= 8 },
    { key: 'upper', label: 'Uma letra maiúscula', test: (p) => /[A-Z]/.test(p) },
    { key: 'lower', label: 'Uma letra minúscula', test: (p) => /[a-z]/.test(p) },
    { key: 'number', label: 'Um número', test: (p) => /[0-9]/.test(p) },
    { key: 'special', label: 'Um caractere especial (!@#$%^&*)', test: (p) => /[!@#$%^&*]/.test(p) },
];

const getPasswordStrength = (password) => {
    const score = REQUIREMENTS.filter((req) => req.test(password || '')).length;

    if (score <= 1) return { percent: 20, text: 'Senha muito fraca', color: '#ff4d4f' };
    if (score === 2) return { percent: 40, text: 'Senha fraca', color: '#faad14' };
    if (score === 3) return { percent: 60, text: 'Senha média', color: '#1890ff' };
    if (score === 4) return { percent: 80, text: 'Quase lá — falta 1 requisito', color: '#1890ff' };
    return { percent: 100, text: 'Senha forte', color: '#52c41a' };
};

const PasswordForm = ({
    form: { getFieldDecorator, validateFields },
    signupData,
    setSignupData,
    setProgress,
}) => {
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    const isValid = !validatePassword(password);
    const strength = getPasswordStrength(password);

    const handleSubmit = (e) => {
        if (e) {
            e.preventDefault();
        }

        validateFields((err, values) => {
            if (!err && values.password && !validatePassword(values.password)) {
                const updatedSignupData = { ...signupData, ...values };
                setSignupData(updatedSignupData);
                setProgress(100);
            }
        });
    };

    useEffect(() => {
        const handleGlobalKeyPress = (e) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                handleSubmit(e);
            }
        };

        window.addEventListener('keydown', handleGlobalKeyPress);

        return () => {
            window.removeEventListener('keydown', handleGlobalKeyPress);
        };
    }, []);

    const goBack = () => setProgress(30);

    return (
        <PageShell>
            <FormCard>
                <StepBadge>Passo 2 de 2</StepBadge>
                <CardTitle>Defina sua senha</CardTitle>
                <CardSubtitle>Último passo para criar sua conta.</CardSubtitle>

                <SummaryBox>
                    <div className="summary-lines">
                        <div className="summary-company">{signupData.company_name}</div>
                        <div className="summary-email">{signupData.email}</div>
                    </div>
                    <Button size="small" onClick={goBack}>
                        <Icon type="edit" /> Editar
                    </Button>
                </SummaryBox>

                <Form onSubmit={handleSubmit}>
                    <FieldLabel htmlFor="password">Senha</FieldLabel>
                    <Form.Item style={{ marginBottom: 0 }}>
                        {getFieldDecorator('password', {
                            rules: [{ required: true, message: 'Informe a senha' }],
                        })(
                            <StyledInput
                                id="password"
                                type={showPassword ? 'text' : 'password'}
                                autoFocus
                                placeholder="Crie uma senha segura"
                                prefix={<Icon type="lock" />}
                                suffix={
                                    <Icon
                                        type={showPassword ? 'eye-invisible' : 'eye'}
                                        style={{ cursor: 'pointer', color: '#8c8c9a' }}
                                        onClick={() => setShowPassword(!showPassword)}
                                    />
                                }
                                onChange={(e) => setPassword(e.target.value)}
                            />
                        )}
                    </Form.Item>

                    {password.length > 0 && (
                        <div style={{ marginTop: 12 }}>
                            <Progress
                                percent={strength.percent}
                                showInfo={false}
                                strokeColor={strength.color}
                                size="small"
                            />
                            <div style={{ color: strength.color, marginTop: 4, fontSize: 13, fontWeight: 500 }}>
                                {strength.text}
                            </div>
                        </div>
                    )}

                    <ReqList>
                        {REQUIREMENTS.map((req) => {
                            const ok = req.test(password);
                            return (
                                <ReqItem key={req.key} ok={ok}>
                                    <Icon type={ok ? 'check-circle' : 'minus-circle'} theme={ok ? 'filled' : 'outlined'} />
                                    {req.label}
                                </ReqItem>
                            );
                        })}
                    </ReqList>

                    <StyledButton
                        type="primary"
                        htmlType="submit"
                        block
                        disabled={!isValid}
                        style={{ marginTop: 24 }}
                        onClick={handleSubmit}
                    >
                        Criar minha conta <Icon type="arrow-right" />
                    </StyledButton>
                </Form>

                <Button type="link" block style={{ marginTop: 8, color: '#6b6b7b' }} onClick={goBack}>
                    <Icon type="arrow-left" /> Voltar
                </Button>
            </FormCard>
        </PageShell>
    )
};

PasswordForm.propTypes = {
    form: PropTypes.shape({
        getFieldDecorator: PropTypes.func.isRequired,
        validateFields: PropTypes.func.isRequired,
    }).isRequired,
    signupData: PropTypes.object.isRequired,
    setSignupData: PropTypes.func.isRequired,
    setProgress: PropTypes.func.isRequired,
};

export default Form.create({ name: 'onboarding' })(PasswordForm);
